import { Controller } from '@hotwired/stimulus';

const trashIcon = `
<?xml version="1.0" encoding="utf-8"?>
<!-- Generator: Adobe Illustrator 23.0.4, SVG Export Plug-In . SVG Version: 6.00 Build 0)  -->
<svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
	 viewBox="0 0 100 100.5" style="enable-background:new 0 0 100 100.5;" xml:space="preserve">
<g id="Layer_2">
</g>
<g id="Layer_1">
	<g>
		<path d="M37.7,85.6L37.7,85.6c1.5-0.1,2.5-1.2,2.4-2.6l-2.5-43.7c-0.1-1.4-1.2-2.4-2.6-2.3c-1.4,0.1-2.4,1.2-2.3,2.6l2.5,43.7
			C35.3,84.5,36.4,85.6,37.7,85.6z"/>
		<path d="M50,85.6c1.4,0,2.5-1.1,2.5-2.5V39.4c0-1.4-1.1-2.5-2.5-2.5s-2.5,1.1-2.5,2.5v43.7C47.5,84.5,48.6,85.6,50,85.6z"/>
		<path d="M62.1,85.6L62.1,85.6c1.4,0,2.5-1,2.6-2.3l2.5-43.7c0.1-1.4-1-2.5-2.3-2.6c-1.4-0.1-2.5,1-2.6,2.3L59.8,83
			C59.7,84.3,60.8,85.5,62.1,85.6z"/>
		<path d="M83.2,13.9H63.1l-1.4-6.4c-0.6-2.8-3.2-4.9-6.1-4.9L44.4,2.5l0,0c-2.9,0-5.4,2-6.1,4.8l-1.5,6.6h-20c-3.3,0-6,2.7-6,6V24
			c0,3.3,2.7,6,6,6h0.9L22,88.7c0.4,4.9,4.5,8.8,9.4,8.8h37.1c4.9,0,9.1-3.9,9.4-8.8l4.3-58.8h0.9c3.3,0,6-2.7,6-6v-4.1
			C89.2,16.6,86.5,13.9,83.2,13.9z M43.1,8.4c0.1-0.6,0.7-1,1.3-1l0,0l11.3,0.1c0.6,0,1.1,0.4,1.3,1l1.2,5.4H41.9L43.1,8.4z
			 M73.1,88.4c-0.2,2.4-2.2,4.2-4.5,4.2H31.5c-2.4,0-4.3-1.8-4.5-4.2L22.7,30h54.6L73.1,88.4z M84.2,24c0,0.6-0.5,1-1,1H16.8
			c-0.6,0-1-0.5-1-1v-4.1c0-0.6,0.5-1,1-1h66.4c0.6,0,1,0.5,1,1V24z"/>
	</g>
</g>
</svg>
`;

// Row fields that carry presentational state rather than filter criteria.
// `error` holds the full validation message and `comment` is free-form user
// text, so both can be arbitrarily long — they must never reach the
// autocomplete query string.
const NON_FILTERING_FIELDS = ['error', 'comment', 'valid', 'id'];

// The starting state of an untouched grid. Built fresh on each call: Tabulator mutates the
// row objects it is handed, so a shared array would carry the previous grid's edits over.
const emptyRows = () => [{}, {}, {}];

// Shared by the key builder and the cleanup of drafts saved under the old, unscoped name.
const DRAFT_KEY_PREFIX = 'bulk_import_data_';

// Connects to data-controller="bulk-import"
export default class extends Controller {
  static values = {
    columnData: Array,
    data: Array,
    showError: Boolean,
    tab: Number,
    consumptionType: String,
    deleteAllConfirm: String,
    deleteSelectedConfirm: String,
    businessId: Number,
    carbonFootprintId: Number,
  };
  static targets = ['table', 'importData'];

  connect() {
    this.discardUnscopedDrafts();

    const errorPopupFormatter = (e, row, onRendered) => {
      const data = row.getData();

      if (data.valid === undefined) {
        return 'Fill in the fields to validate';
      }

      if (data.valid) {
        return 'No errors';
      }

      return data.error;
    };

    const valuesLookup = async (cell, filterTerm) => {
      const rowData = cell.getRow().getData();
      const columnUrl = cell.getColumn().getDefinition().valuesURL;
      const valuesUrl = new URL(columnUrl, window.location.origin);
      valuesUrl.searchParams.append('term', filterTerm);
      // Only forward fields the emission-factor filter actually narrows on.
      // Sending the whole row appended the (arbitrarily long) validation error
      // text and free-form comment to the URL, pushing `user_return_to` past
      // the 4KB cookie limit — ActionDispatch::Cookies::CookieOverflow, Sentry
      // RUBY-RAILS-4T.
      Object.entries(rowData).forEach(([key, value]) => {
        if (value && !NON_FILTERING_FIELDS.includes(key)) {
          valuesUrl.searchParams.append(key, value);
        }
      });

      return this.cachedLookup(valuesUrl);
    };

    const parsedColumnData = this.columnDataValue.map((column) => {
      if (column.cellClick === 'delete') {
        column.cellClick = (e, cell) => {
          e.stopPropagation();
          cell.getRow().delete();
          this.saveData();
        };
        column.formatter = () =>
          `<div class="delete d-flex justify-content-center align-items-center h-100">${trashIcon}</div>`;
      }

      if (column.editor !== 'list') {
        return column;
      }

      const newColumn = { ...column };

      if (column.field === 'scope') {
        newColumn.cellEdited = this.handleScopeChange;
      }

      if (column.field === 'country') {
        newColumn.cellEdited = this.handleCountryChange;
      }

      if (column.field === 'transport_scope') {
        newColumn.cellEdited = this.handleScopeChange;
      }

      newColumn.editorParams.valuesLookup = valuesLookup;
      return newColumn;
    });

    if (this.showErrorValue) {
      parsedColumnData[0].clickPopup = errorPopupFormatter;
    }

    this.table = new Tabulator(this.tableTarget, {
      data: this.retrieveData(),
      columns: parsedColumnData,
      clipboard: true,
      clipboardPasteParser: 'range',
      selectableRange: 1,
      selectableRangeColumns: true,
      selectableRangeRows: true,
      selectableRangeClearCells: true,
      layout: 'fitColumns',
      height: '100%',
      clipboardPasteAction: (data) => {
        let rows = [],
          range = this.table.modules.selectRange.activeRange,
          singleCell = false,
          bounds,
          startCell,
          startRow,
          rowWidth,
          dataLength;

        dataLength = data.length;

        if (range) {
          bounds = range.getBounds();
          startCell = bounds.start;

          if (bounds.start === bounds.end) {
            singleCell = true;
          }

          if (startCell) {
            rows = this.table.rowManager.activeRows.slice();
            startRow = rows.indexOf(startCell.row);

            if (singleCell) {
              rowWidth = data.length;
            } else {
              rowWidth = rows.indexOf(bounds.end.row) - startRow + 1;
            }

            if (startRow > -1) {
              const updatedRows = rows.slice(startRow, startRow + rowWidth);
              const missingData = data.slice(updatedRows.length, dataLength);

              // Pasting used to restore the redraw straight away and then re-style every row from
              // its own promise callback, so each row was rendered and then touched again.
              // Keep the redraw blocked until every row has its data, then style them once.
              this.table.blockRedraw();

              Promise.all([
                Promise.all(updatedRows.map((row, i) => row.updateData(data[i % dataLength]))),
                this.table.addData(missingData),
              ])
                .then(([, addedRows]) => [...updatedRows, ...addedRows])
                .finally(() => this.table.restoreRedraw())
                .then((touchedRows) => {
                  touchedRows.forEach((row) => this.applyConditionalDisabling(row));
                });
            }
          }
        }

        return rows;
      },

      //change edit trigger mode to make cell navigation smoother
      editTriggerEvent: 'dblclick',
      clipboardCopyRowRange: 'range',
      clipboardCopyConfig: {
        rowHeaders: false, //do not include row headers in clipboard output
        columnHeaders: false, //do not include column headers in clipboard output
      },
      clipboardCopyStyled: false,
      validationMode: 'highlight',
    });

    // Clicking a cell (and finishing an edit) makes Tabulator focus the rows holder so the
    // keyboard keeps working, but without `preventScroll`. The holder is taller than the modal,
    // so the browser scrolls the modal to bring the holder's top into view - jumping back to
    // the top of the grid while working further down it.
    const rowsHolder = this.table.rowManager.element;
    const focusRowsHolder = rowsHolder.focus.bind(rowsHolder);
    rowsHolder.focus = (options) => focusRowsHolder({ preventScroll: true, ...options });

    this.table.on('cellEdited', () => {
      this.saveData();
    });

    this.table.on('clipboardPasted', () => {
      this.table.validate();
      this.saveData();
    });

    // Pasting or clearing cells makes Tabulator empty and refill its rows. The grid does not
    // scroll itself (its holder is overflow-y: hidden); the modal around it does, and while the
    // rows are gone the modal's content is too short, so the browser resets its scroll to the top.
    this.table.on('renderStarted', () => {
      this.scrollTopBeforeRender = this.scrollContainer?.scrollTop;
    });

    this.table.on('renderComplete', () => {
      if (this.scrollContainer && this.scrollTopBeforeRender !== undefined) {
        this.scrollContainer.scrollTop = this.scrollTopBeforeRender;
      }
    });

    this.table.on('tableBuilt', () => {
      // `fitColumns` only sets the starting widths. Freezing them means resizing one column
      // no longer squeezes the others to compensate: the grid scrolls horizontally instead.
      const widths = this.table.getColumns().map((column) => column.getWidth());
      this.table.getColumns().forEach((column, i) => column.setWidth(widths[i]));

      setTimeout(() => {
        this.disableInvalidCells();
      }, 0);
    });

    this.element.addEventListener('turbo:submit-end', this.handleFormSubmitEnd.bind(this));
  }

  // The answer to a lookup depends only on its URL (column, term and the row's filtering fields),
  // and the reference data behind it does not change while the grid is open. Reopening a cell,
  // retyping a term or filling many rows alike used to hit the server, which runs an emission
  // factor filter and a trigram search for each request. The promise is cached, not the result,
  // so two identical requests in flight share one fetch; a failure is evicted so it can be retried.
  cachedLookup(url) {
    this.lookupCache ||= new Map();
    const key = url.toString();

    if (!this.lookupCache.has(key)) {
      const request = fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(`Lookup failed: ${response.status}`);
          return response.json();
        })
        .catch((error) => {
          this.lookupCache.delete(key);
          throw error;
        });
      this.lookupCache.set(key, request);
    }

    return this.lookupCache.get(key);
  }

  disableInvalidCells() {
    const rows = this.table.getRows();

    rows.forEach((row, index) => {
      try {
        this.applyConditionalDisabling(row);
      } catch (error) {
        console.error(`Error processing row ${index}:`, error);
      }

      const transportScopeCell = row.getCell('transport_scope');
      if (transportScopeCell) {
        this.handleScopeChange(transportScopeCell);
      }
    });
  }

  submit(event) {
    // Clean empty rows, ignoring stale valid/error metadata echoed back from a
    // previous failed submission so rows with no real user input are still
    // recognized as empty and removed.
    this.table.getRows().forEach((row) => {
      const data = row.getData();

      const isEmpty = Object.entries(data).every(
        ([key, value]) => ['valid', 'error'].includes(key) || !value
      );

      if (isEmpty) {
        row.delete();
      }
    });

    const invalidCells = [];

    this.table.getRows().forEach((row) => {
      row.getCells().forEach((cell) => {
        const definition = cell.getColumn().getDefinition();

        if (definition.validator === 'required') {
          const value = cell.getValue();

          const isDisabled = cell.getElement().classList.contains('disabled');

          if (!isDisabled && (value === '' || value === null || value === undefined)) {
            cell.getElement().classList.add('tabulator-validation-fail');
            invalidCells.push(cell);
          } else {
            cell.getElement().classList.remove('tabulator-validation-fail');
          }
        }
      });
    });

    if (invalidCells.length > 0) {
      event.preventDefault();

      alert('Please complete all required fields before submitting.');
      return;
    }

    const data = JSON.stringify(this.table.getData());
    this.importDataTarget.value = data;
  }

  addRow(event) {
    event.preventDefault();

    this.table.addRow({}, false);
  }

  // Emptying the grid one trash icon at a time is the only way back to a clean sheet today,
  // and the rows people most want gone are the ones they did not type: the grid is restored
  // from localStorage every time the modal opens, and that draft is only cleared on a
  // successful submit, so abandoning the modal leaves it behind for good.
  //
  // This clears the grid and the draft behind it. Rows already saved to the carbon footprint
  // are untouched - those have their own per-row delete in the tab's table.
  deleteAllRows(event) {
    event.preventDefault();

    if (!window.confirm(this.deleteAllConfirmValue)) return;

    this.table.clearData();
    this.table.addData(emptyRows());
    this.dataValue = []; // so a failed submission's rows are not restored on the next open
    this.importDataTarget.value = '[]';
    localStorage.removeItem(this.localStorageKey);
  }

  // The trash icon removes one row at a time. This removes every row the selected range
  // touches - drag or shift+click across the rows, one cell per row is enough. The grid's
  // range selection is kept rather than adding a checkbox column: Tabulator cannot combine
  // row selection with range selection, which copy/paste and cell clearing depend on.
  deleteSelectedRows(event) {
    event.preventDefault();

    const rows = [...new Set(this.table.getRanges().flatMap((range) => range.getRows()))];
    if (rows.length === 0) return;

    if (!window.confirm(this.deleteSelectedConfirmValue.replace('{count}', rows.length))) return;

    this.table.deleteRow(rows).then(() => {
      // Ranges are kept as row indexes, so after a delete they would point at other rows.
      this.table.modules.selectRange.resetRanges();

      if (this.table.getRows().length === 0) {
        this.table.addData(emptyRows());
      }

      this.saveData();
    });
  }

  retrieveData() {
    if (this.dataValue.length > 0) {
      return this.dataValue;
    }

    const savedData = localStorage.getItem(this.localStorageKey);

    try {
      if (savedData) {
        return JSON.parse(savedData);
      }
    } catch (e) {
      console.error('Error parsing saved data:', e);
    }

    return emptyRows();
  }

  saveData() {
    localStorage.setItem(this.localStorageKey, JSON.stringify(this.table.getData()));
  }

  handleCountryChange = (cell) => {
    const value = cell.getValue();
    const row = cell.getRow();

    if (value === 'United States of America') {
      this.enableCell('state', row);

      return;
    }

    this.disableCell('state', row);
  };

  handleScopeChange = (cell) => {
    const value = cell.getValue();
    const row = cell.getRow();

    if (['Scope 3', 'Business travel', 'Staff commute', 'Freighting goods'].includes(value)) {
      this.enableCell('ghg_category', row);

      return;
    }

    this.disableCell('ghg_category', row);
  };

  disableCell(field, row) {
    try {
      const cell = row.getCell(field);
      if (cell) {
        const cellElement = cell.getElement();
        if (cellElement) {
          cellElement.classList.add('disabled');

          // Most pasted rows have nothing in a disabled field, and rewriting it still costs a
          // row update and a cell render each.
          if (cell.getValue()) {
            row.update({ [field]: '' });
            cell.render();
          }
        }
      }
    } catch (error) {
      console.error(`Error disabling cell ${field}:`, error);
    }
  }

  enableCell(field, row) {
    try {
      const cell = row.getCell(field);
      if (cell) {
        const cellElement = cell.getElement();
        if (cellElement) {
          cellElement.classList.remove('disabled');
          cell.render();
        }
      }
    } catch (error) {
      console.error(`Error enabling cell ${field}:`, error);
    }
  }

  handleFormSubmitEnd(event) {
    const { success } = event.detail;

    if (success) {
      localStorage.removeItem(this.localStorageKey);
    }
  }

  applyConditionalDisabling(row) {
    const data = row.getData();

    try {
      const scopeCell = row.getCell('scope');
      if (scopeCell) {
        if (data.scope === 'Scope 3') {
          this.enableCell('ghg_category', row);
        } else {
          this.disableCell('ghg_category', row);
        }
      }
    } catch (error) {
      console.error('Error processing scope:', error);
    }

    try {
      const countryCell = row.getCell('country');
      if (countryCell) {
        if (data.country === 'United States of America') {
          this.enableCell('state', row);
        } else {
          this.disableCell('state', row);
        }
      }
    } catch (error) {
      console.error('Error processing country:', error);
    }

    const transportScopeCell = row.getCell('transport_scope');
    if (transportScopeCell) {
      if (data.transport_scope !== 'Owned fleet') {
        this.enableCell('ghg_category', row);
      } else {
        this.disableCell('ghg_category', row);
      }
    }
  }

  // The closest ancestor that actually scrolls: the modal's content box, not the grid.
  get scrollContainer() {
    let element = this.tableTarget.parentElement;
    while (element && !['auto', 'scroll'].includes(getComputedStyle(element).overflowY)) {
      element = element.parentElement;
    }
    return element;
  }

  // The draft is scoped to the footprint it was typed into. It used to be keyed by tab alone,
  // so on one browser the grid restored on client B whatever had been left unsaved on client
  // A's tab of the same number - and saving from there imported A's rows into B's footprint.
  // Reported 06/08/2026. The business/footprint pair is what makes the key unambiguous: a
  // client's 2024 and 2025 footprints are as distinct as two clients are.
  get localStorageKey() {
    let key = `${DRAFT_KEY_PREFIX}b${this.businessIdValue}_cf${this.carbonFootprintIdValue}_${this.tabValue}`;
    if (this.hasConsumptionTypeValue && this.consumptionTypeValue) {
      key += `_${this.consumptionTypeValue}`;
    }
    return key;
  }

  // Scoping the key does nothing for the drafts already sitting in people's browsers under the
  // old tab-only name: those would stay there, unreachable and unexpiring. The old names are
  // `bulk_import_data_<tab>...`, so anything that does not carry the `b<id>` scope marker is
  // one of them, and is dropped the first time the grid opens.
  discardUnscopedDrafts() {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(DRAFT_KEY_PREFIX) && !key.startsWith(`${DRAFT_KEY_PREFIX}b`))
      .forEach((key) => localStorage.removeItem(key));
  }
}
